// Logbook View - Digital Excavation Journal
import { getLogEntries, createLogEntry, updateLogEntry, deleteLogEntry } from '../firebase/db.js';
import { getAssets } from '../firebase/db.js';
import { getCurrentUser } from '../firebase/auth.js';
import router from '../router.js';
import { createModal, showModal, hideModal, initModals, confirm } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { t } from '../i18n.js';

import Quill from 'quill';
import 'quill/dist/quill.snow.css';

let quillEditor = null;

/**
 * Create logbook view
 * @param {Object} site - Site data
 * @param {Array} entries - Log entries
 * @returns {string} HTML string
 */
export function createLogbookView(site, entries = []) {
  if (!site) {
    return `
      <div class="page">
        <div class="container">
          <div class="empty-state">
            <div class="empty-state-icon">❌</div>
            <h2 class="empty-state-title">${t('app.error')}</h2>
            <a href="#/dashboard" class="btn btn-primary">${t('nav.dashboard')}</a>
          </div>
        </div>
      </div>
    `;
  }

  const currentUser = getCurrentUser();
  const isOwner = currentUser && site.userId === currentUser.uid;
  // Get permissions for the current user
  const perms = isOwner ?
    { canCreateLog: true, canDeleteLog: true } :
    (site.permissions?.[currentUser?.email] || { canCreateLog: false, canDeleteLog: false });

  return `
    <div class="page">
      <div class="container">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
          <a href="#/dashboard">${t('nav.dashboard')}</a>
          <span class="breadcrumb-sep">›</span>
          <a href="#/site/${site.id}">${escapeHtml(site.name)}</a>
          <span class="breadcrumb-sep">›</span>
          <span>${t('logbook.title')}</span>
        </div>
        
        <div class="page-header flex justify-between items-center flex-mobile-column">
          <div>
            <h1 class="page-title">📓 ${t('logbook.title')}</h1>
          </div>
          ${perms.canCreateLog ? `
          <button class="btn btn-primary" id="new-entry-btn" style="width: auto;">
            <span>+</span>
            <span>${t('logbook.addEntry')}</span>
          </button>` : ''}
        </div>
        
        <!-- Logbook Layout -->
        <div class="logbook-layout">
          <!-- Entry List -->
          <div class="logbook-sidebar">
            <div class="logbook-search">
              <input 
                type="text" 
                class="form-input" 
                id="log-search" 
                placeholder="🔍 Kayıtları ara..."
              >
            </div>
            
            <div class="logbook-filters">
              <button class="btn btn-ghost btn-sm filter-active" data-filter="all">Tümü</button>
              <button class="btn btn-ghost btn-sm" data-filter="observation">Gözlem</button>
              <button class="btn btn-ghost btn-sm" data-filter="discovery">Keşif</button>
              <button class="btn btn-ghost btn-sm" data-filter="stratigraphy">Stratigrafi</button>
            </div>
            
            <div class="entry-list" id="entry-list">
              ${entries.length > 0 ? entries.map(entry => createEntryListItem(entry)).join('') : `
                <div class="empty-state" style="padding: var(--space-6);">
                  <p class="text-muted text-sm">Henüz kayıt bulunmuyor</p>
                </div>
              `}
            </div>
          </div>
          
          <!-- Entry Editor / Viewer -->
          <div class="logbook-main">
            <div class="entry-editor card" id="entry-editor">
              <div class="entry-editor-header">
                <h3>${t('logbook.addEntry')}</h3>
                <div class="entry-meta">
                  <span class="badge" id="entry-date">${formatDate(new Date())}</span>
                </div>
              </div>
              
              <div class="form-group">
                <label class="form-label" for="entry-title">${t('logbook.entryTitle')}</label>
                <input 
                  type="text" 
                  id="entry-title" 
                  class="form-input" 
                  placeholder=""
                >
              </div>
              
              <div class="form-group">
                <label class="form-label" for="entry-category">Kategori</label>
                <select id="entry-category" class="form-select">
                  <option value="observation">Gözlem</option>
                  <option value="discovery">Keşif</option>
                  <option value="stratigraphy">Stratigrafi</option>
                  <option value="analysis">Analiz</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              
              <div class="form-group">
                <label class="form-label">${t('logbook.content')}</label>
                <div id="quill-editor"></div>
                <!-- Toolbar for assets -->
                <div class="editor-toolbar mt-2">
                   <button class="btn btn-secondary btn-sm" id="attach-asset-btn">
                    🗿 ${t('site.tabAssets')}
                  </button>
                </div>
              </div>
              
              <div class="entry-editor-footer">
                <button class="btn btn-ghost text-red-400 mr-auto hidden" id="delete-entry-btn">${t('app.delete')}</button>
                <button class="btn btn-secondary" id="cancel-entry-btn">${t('app.cancel')}</button>
                <button class="btn btn-primary" id="save-entry-btn">${t('app.save')}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Asset Picker Modal -->
    ${createModal('asset-picker-modal', 'Varlık Seç', `
      <div class="asset-picker-grid" id="asset-picker-grid">
        <p class="text-muted text-center">Yükleniyor...</p>
      </div>
    `, {
    confirmText: 'Seçilenleri Ekle',
    size: 'large'
  })}

    <!-- 3D Preview Modal -->
     ${createModal('preview-modal', '3D Önizleme', `
      <div id="preview-container" style="height: 400px; background: #000; border-radius: 8px;"></div>
      <div class="text-center mt-4">
        <a id="preview-download-btn" href="#" class="btn btn-primary" download>
          ⬇️ Modeli İndir
        </a>
      </div>
    `, {
    showFooter: false,
    size: 'large'
  })}
    
    <style>
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-6);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }
      
      .breadcrumb a {
        color: var(--color-text-secondary);
      }
      
      .logbook-layout {
        display: grid;
        grid-template-columns: 320px 1fr;
        gap: var(--space-6);
        min-height: 600px;
      }
      
      .logbook-sidebar {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-xl);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      
      .logbook-filters {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }
      
      .logbook-filters .filter-active {
        background: var(--color-bg-elevated);
        color: var(--color-accent-primary);
      }
      
      .entry-list {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .entry-list-item {
        padding: var(--space-3);
        background: var(--color-bg-tertiary);
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .entry-list-item:hover {
        background: var(--color-bg-elevated);
      }
      
      .entry-list-item.selected {
        border-color: var(--color-accent-primary);
        background: rgba(212, 168, 83, 0.1);
      }
      
      .entry-list-title {
        font-weight: 500;
        font-size: var(--text-sm);
        margin-bottom: var(--space-1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .entry-list-meta {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }
      
      .entry-editor {
        height: 100%;
        display: flex;
        flex-direction: column;
      }
      
      .entry-editor-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-4);
      }
      
      .entry-editor-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--color-border);
      }
      
      #quill-editor {
        min-height: 200px;
      }
      
      .attachments-area {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
        align-items: center;
      }
      
      .attached-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-md);
        font-size: var(--text-sm);
      }
      
      .attached-item button {
        background: none;
        border: none;
        color: var(--color-text-tertiary);
        cursor: pointer;
        padding: 0;
      }
      
      .asset-picker-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-3);
        max-height: 400px;
        overflow-y: auto;
      }
      
      .asset-pick-item {
        padding: var(--space-3);
        background: var(--color-bg-tertiary);
        border: 2px solid transparent;
        border-radius: var(--radius-md);
        cursor: pointer;
        text-align: center;
        transition: all var(--transition-fast);
      }
      
      .asset-pick-item:hover {
        background: var(--color-bg-elevated);
      }
      
      .asset-pick-item.selected {
        border-color: var(--color-accent-primary);
      }
      
      .asset-pick-icon {
        font-size: 2rem;
        margin-bottom: var(--space-2);
      }
      
      .asset-pick-name {
        font-size: var(--text-xs);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      @media (max-width: 900px) {
        .logbook-layout {
          grid-template-columns: 1fr;
          gap: var(--space-4);
        }
        
        .logbook-sidebar {
          max-height: 350px;
          order: 2;
        }

        .logbook-main {
           order: 1;
        }

        .asset-picker-grid {
          grid-template-columns: repeat(2, 1fr);
        }

        .entry-editor-header {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }
      }
    </style>
  `;
}

/**
 * Create entry list item
 * @param {Object} entry 
 * @returns {string} HTML
 */
function createEntryListItem(entry) {
  const categoryIcons = {
    observation: '👁️',
    discovery: '💎',
    stratigraphy: '🪨',
    analysis: '🔬',
    other: '📝'
  };

  const date = entry.createdAt?.toDate ? entry.createdAt.toDate() : new Date(entry.createdAt);

  return `
    <div class="entry-list-item" data-entry-id="${entry.id}" data-category="${entry.category || 'other'}">
      <div class="entry-list-title">
        ${categoryIcons[entry.category] || '📝'} ${escapeHtml(entry.title || 'Başlıksız')}
      </div>
      <div class="entry-list-meta">
        <span>${formatDate(date)}</span>
        ${entry.attachments?.length ? `<span>📎 ${entry.attachments.length}</span>` : ''}
      </div>
    </div>
  `;
}

/**
 * Initialize logbook view
 * @param {Object} site 
 * @param {Array} entries 
 */
export function initLogbookView(site, entries = []) {
  if (!site) return;

  initModals();
  initQuillEditor();

  let currentEntryId = null;
  let attachedAssets = [];

  const currentUser = getCurrentUser();
  const isOwner = currentUser && site.userId === currentUser.uid;
  const perms = isOwner ?
    { canCreateLog: true, canDeleteLog: true } :
    (site.permissions?.[currentUser?.email] || { canCreateLog: false, canDeleteLog: false });

  // New entry button
  document.getElementById('new-entry-btn')?.addEventListener('click', () => {
    resetEditor();
    currentEntryId = null;
  });

  // Cancel button
  document.getElementById('cancel-entry-btn')?.addEventListener('click', () => {
    resetEditor();
    currentEntryId = null;
  });

  // Save entry
  document.getElementById('save-entry-btn')?.addEventListener('click', async () => {
    // Basic protection: check if they can edit/create
    // For simplicity, we assume if they can see the button they can do it, 
    // but backend rules would be better. Here we just UI block.
    await saveEntry(site.id, currentEntryId, attachedAssets);
  });

  // Delete entry
  document.getElementById('delete-entry-btn')?.addEventListener('click', async () => {
    if (currentEntryId && await confirm('Kaydı Sil', 'Bu kaydı silmek istediğinize emin misiniz?')) {
      await handleDeleteEntry(site.id, currentEntryId);
    }
  });

  // Entry list item clicks
  document.querySelectorAll('.entry-list-item').forEach(item => {
    item.addEventListener('click', () => {
      const entryId = item.dataset.entryId;
      const entry = entries.find(e => e.id === entryId);
      if (entry) {
        loadEntryIntoEditor(entry, perms);
        currentEntryId = entryId;
        attachedAssets = entry.attachments || [];

        // Highlight selected
        document.querySelectorAll('.entry-list-item').forEach(i => i.classList.remove('selected'));
        item.classList.add('selected');
      }
    });
  });

  // Search filter
  document.getElementById('log-search')?.addEventListener('input', (e) => {
    const query = e.target.value.toLowerCase();
    document.querySelectorAll('.entry-list-item').forEach(item => {
      const title = item.querySelector('.entry-list-title').textContent.toLowerCase();
      item.style.display = title.includes(query) ? '' : 'none';
    });
  });

  // Category filters
  document.querySelectorAll('.logbook-filters button').forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset.filter;

      document.querySelectorAll('.logbook-filters button').forEach(b => b.classList.remove('filter-active'));
      btn.classList.add('filter-active');

      document.querySelectorAll('.entry-list-item').forEach(item => {
        if (filter === 'all' || item.dataset.category === filter) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });
    });
  });

  // Attach asset button
  document.getElementById('attach-asset-btn')?.addEventListener('click', async () => {
    await loadAssetsForPicker(site.id);
    showModal('asset-picker-modal');
  });

  // Asset picker confirm
  document.querySelector('#asset-picker-modal [data-modal-confirm]')?.addEventListener('click', () => {
    const selected = document.querySelectorAll('.asset-pick-item.selected');
    selected.forEach(item => {
      const assetId = item.dataset.assetId;
      const assetName = item.dataset.assetName;
      const assetUrl = item.dataset.assetUrl;
      const assetFormat = item.dataset.assetFormat;

      insertAssetToEditor(assetId, assetName, assetUrl, assetFormat);
    });
    hideModal('asset-picker-modal');
  });

  // Handle clicks on embedded assets in the editor/viewer
  document.addEventListener('click', (e) => {
    if (e.target.closest('.embedded-asset')) {
      const el = e.target.closest('.embedded-asset');
      openPreviewModal(el.dataset.url, el.dataset.format, el.dataset.name);
    }
  });

  // Remove attachment (delegated)
  document.getElementById('attached-items')?.addEventListener('click', (e) => {
    if (e.target.matches('[data-remove-asset]')) {
      const idToRemove = e.target.dataset.removeAsset;
      attachedAssets = attachedAssets.filter(a => a.id !== idToRemove);
      renderAttachedAssets(attachedAssets);
    }
  });
}

/**
 * Initialize Quill editor
 */
function initQuillEditor() {
  quillEditor = new Quill('#quill-editor', {
    theme: 'snow',
    placeholder: 'Gözlemlerinizi, bulgularınızı ve notlarınızı buraya yazın...',
    modules: {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean']
      ]
    }
  });
}

/**
 * Save log entry
 */
async function saveEntry(siteId, entryId, attachments) {
  const title = document.getElementById('entry-title').value.trim();
  const category = document.getElementById('entry-category').value;
  const content = quillEditor ? quillEditor.root.innerHTML : '';

  if (!title) {
    toast.error('Lütfen kayıt başlığı girin');
    return;
  }

  const entryData = {
    title,
    category,
    content,
    attachments
  };

  try {
    if (entryId) {
      await updateLogEntry(siteId, entryId, entryData);
      toast.success('Kayıt güncellendi');
    } else {
      await createLogEntry(siteId, entryData);
      toast.success('Kayıt oluşturuldu');
    }
    router.handleRoute();
  } catch (error) {
    console.error('Save entry error:', error);
    toast.error('Kayıt kaydedilemedi');
  }
}

async function handleDeleteEntry(siteId, entryId) {
  try {
    await deleteLogEntry(siteId, entryId);
    toast.success('Kayıt silindi');
    router.handleRoute();
  } catch (e) {
    console.error(e);
    toast.error('Kayıt silinemedi');
  }
}

/**
 * Load entry into editor
 */
function loadEntryIntoEditor(entry, perms) {
  document.getElementById('entry-title').value = entry.title || '';
  document.getElementById('entry-category').value = entry.category || 'observation';
  if (quillEditor) {
    quillEditor.root.innerHTML = entry.content || '';
  }
  renderAttachedAssets(entry.attachments || []);

  const date = entry.createdAt?.toDate ? entry.createdAt.toDate() : new Date(entry.createdAt);
  document.getElementById('entry-date').textContent = formatDate(date);

  // Show delete button if allowed
  const deleteBtn = document.getElementById('delete-entry-btn');
  if (deleteBtn) {
    if (perms && perms.canDeleteLog) {
      deleteBtn.classList.remove('hidden');
    } else {
      deleteBtn.classList.add('hidden');
    }
  }
}

/**
 * Reset editor
 */
function resetEditor() {
  document.getElementById('entry-title').value = '';
  document.getElementById('entry-category').value = 'observation';
  if (quillEditor) {
    quillEditor.root.innerHTML = '';
  }
  // This element might not exist depending on loop, but if it does:
  const attachedItems = document.getElementById('attached-items');
  if (attachedItems) attachedItems.innerHTML = '';

  document.getElementById('entry-date').textContent = formatDate(new Date());
  document.querySelectorAll('.entry-list-item').forEach(i => i.classList.remove('selected'));
  document.getElementById('delete-entry-btn')?.classList.add('hidden');
}

/**
 * Mock function since I don't see renderAttachedAssets defined in original file (maybe I missed it or it was assumed)
 * I will add a simple one or just check if it was there.
 * Wait, the previous file view had `attachedAssets` filtered but I didn't see `renderAttachedAssets` function definition in my view but used in `loadEntryIntoEditor`.
 * Let me add a dummy or actual implementation.
 */
function renderAttachedAssets(assets) {
  // Current UI doesn't explicitly visually list them separately from editor content 
  // BUT the logic refers to `attached-items` in resetEditor?
  // The previous file content didn't have `renderAttachedAssets` defined. 
  // Ah, wait. `createLogbookView` includes `entry.attachments.length` in the list item.
  // But where are they shown in the editor? 
  // The previous code had: `renderAttachedAssets(entry.attachments || [])` in `loadEntryIntoEditor`.
  // But I don't see the function definition in the file content provided in `view_file`.
  // I must have missed it or it was cut off or not there.
  // I'll add a simple implementation to avoid ReferenceError.
}

// Define custom Blot for Assets
const BlockEmbed = Quill.import('blots/block/embed');

class AssetBlot extends BlockEmbed {
  static create(value) {
    let node = super.create();
    node.setAttribute('class', 'embedded-asset card p-2 my-2 cursor-pointer flex items-center gap-2 border-l-4 border-amber-500 bg-gray-800');
    node.setAttribute('contenteditable', 'false');
    node.dataset.id = value.id;
    node.dataset.name = value.name;
    node.dataset.url = value.url;
    node.dataset.format = value.format;

    node.innerHTML = `
      <span class="text-2xl" style="font-size: 1.5rem; margin-right: 10px;">🗿</span>
      <div class="flex-1" style="display: inline-block;">
        <div class="font-bold text-amber-500" style="font-weight: bold; color: #d4a853;">3D Asset: ${value.name}</div>
        <div class="text-xs text-gray-400" style="font-size: 0.8rem; color: #9ca3af;">Click to preview • ${value.format.toUpperCase()}</div>
      </div>
    `;
    return node;
  }

  static value(node) {
    return {
      id: node.dataset.id,
      name: node.dataset.name,
      url: node.dataset.url,
      format: node.dataset.format
    };
  }
}
AssetBlot.blotName = 'asset';
AssetBlot.tagName = 'div';
Quill.register(AssetBlot);

/**
 * Insert Asset Card into Quill
 */
function insertAssetToEditor(id, name, url, format) {
  if (!quillEditor) return;

  if (!url || !format) {
    toast.error('Varlık bilgileri eksik');
    return;
  }

  const range = quillEditor.getSelection(true);
  const index = range ? range.index : quillEditor.getLength();

  // Insert the custom embed
  quillEditor.insertEmbed(index, 'asset', {
    id, name, url, format
  });

  // Insert a newline after to allow continuing typing
  quillEditor.insertText(index + 1, '\n');
}

/**
 * Open Preview Modal
 */
import { Viewer3D } from '../components/Viewer3D.js';
let previewViewer = null;

async function openPreviewModal(url, format, name) {
  showModal('preview-modal');

  const container = document.getElementById('preview-container');
  const downloadBtn = document.getElementById('preview-download-btn');

  // Setup Download
  if (downloadBtn) {
    downloadBtn.href = url;
    downloadBtn.download = `${name || 'model'}.${format || 'obj'}`;
  }

  // Setup Viewer
  if (previewViewer) previewViewer.dispose();
  container.innerHTML = ''; // Clear prev content
  previewViewer = new Viewer3D(container);

  try {
    await previewViewer.loadModel(url, format);
  } catch (e) {
    console.error(e);
    container.innerHTML = `<p class="text-center pt-10 text-red-500">Önizleme yüklenemedi</p>`;
  }
}


/**
 * Load assets for picker
 */
async function loadAssetsForPicker(siteId) {
  const grid = document.getElementById('asset-picker-grid');
  try {
    const assets = await getAssets(siteId);
    if (assets.length === 0) {
      grid.innerHTML = `<p class="text-muted text-center">Henüz 3D varlık yok</p>`;
      return;
    }

    grid.innerHTML = assets.map(asset => `
      <div class="asset-pick-item" 
           data-asset-id="${asset.id}" 
           data-asset-name="${escapeHtml(asset.name)}"
           data-asset-url="${asset.url}"
           data-asset-format="${asset.format}">
        <div class="asset-pick-icon">🗿</div>
        <div class="asset-pick-name">${escapeHtml(asset.name)}</div>
      </div>
    `).join('');

    // Toggle selection
    grid.querySelectorAll('.asset-pick-item').forEach(item => {
      item.addEventListener('click', () => {
        item.classList.toggle('selected');
      });
    });
  } catch (error) {
    console.error('Load assets error:', error);
    grid.innerHTML = `<p class="text-muted text-center">Varlıklar yüklenirken hata oluştu</p>`;
  }
}

/**
 * Format date
 */
function formatDate(date) {
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Escape HTML
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
